// Shared shell for every page — top-of-page brand + cross-section nav.
// The Overview tab links back to the dashboard; subpages cover workouts,
// exercises, muscles, and cardio.

import type { ReactNode } from 'react';
import {
  Barbell,
  CalendarBlank,
  ChartLine,
  Heartbeat,
  PersonArmsSpread,
  type Icon,
} from '@phosphor-icons/react';
import { Link, useRoute } from '../lib/router';
import css from './ManageLayout.module.css';

interface Tab {
  to: string;
  label: string;
  icon: Icon;
  exact?: boolean;
}
const TABS: Tab[] = [
  { to: '/', label: 'Overview', icon: ChartLine, exact: true },
  { to: '/manage/days', label: 'Workouts', icon: CalendarBlank },
  { to: '/manage/exercises', label: 'Exercises', icon: Barbell },
  { to: '/manage/muscles', label: 'Muscles', icon: PersonArmsSpread },
  { to: '/cardio', label: 'Cardio', icon: Heartbeat },
];

function isActive(path: string, t: Tab): boolean {
  return t.exact ? path === t.to : path.startsWith(t.to);
}

export function PageLayout({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  const path = useRoute();
  return (
    <div className={css.page}>
      <header className={css.header}>
        <Link to="/" className={css.brand}>
          gymdash
        </Link>
        <nav className={css.nav}>
          {TABS.map((t) => {
            const TabIcon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to}
                className={isActive(path, t) ? `${css.tab} ${css.tabActive}` : css.tab}
              >
                <TabIcon size={15} weight="regular" />
                {t.label}
              </Link>
            );
          })}
        </nav>
      </header>
      {title && <h1 className={css.title}>{title}</h1>}
      <div className={css.content}>{children}</div>
    </div>
  );
}

// Backwards-compat: the manage/* pages still import ManageLayout. Re-export
// PageLayout under the old name so we can swap call sites in a follow-up.
export const ManageLayout = PageLayout;
